import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { CloudUpload, Save, ArrowBack, Delete } from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  createCategory,
  updateCategory,
  getAllServiceLocations,
} from "../../services/Api";

const ModifyCategory = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [isLocationMenuOpen, setIsLocationMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    type: "Grocery",
    order: "1",
    isActive: true,
    locationIds: [],
  });
  const [serviceLocations, setServiceLocations] = useState([]);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const locRes = await getAllServiceLocations(1, 100);
        if (locRes.success) {
          setServiceLocations(
            Array.isArray(locRes.data) ? locRes.data : locRes.data?.docs || [],
          );
        }
      } catch (err) {
        console.error("Failed to fetch locations", err);
      }

      if (isEdit && location.state?.category) {
        const { category } = location.state;
        let parsedLocationIds = [];
        if (category.locations && Array.isArray(category.locations)) {
          parsedLocationIds = category.locations.map(
            (loc) => loc.id || loc._id,
          );
        } else if (category.locationIds) {
          parsedLocationIds = Array.isArray(category.locationIds)
            ? category.locationIds
            : category.locationIds.split(",").filter(Boolean);
        }

        setFormData({
          name: category.name || "",
          type: category.type || "Grocery",
          order: category.order || "1",
          isActive: category.isActive !== undefined ? category.isActive : true,
          locationIds: parsedLocationIds,
        });
        setPreview(category.image);
      }
    };
    fetchInitialData();
  }, [isEdit, location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "order") {
      if (value === "") {
        finalValue = "";
      } else if (String(value).match(/\.\d+/)) {
        // If it has a decimal point followed by digits (e.g., '1.5'), round it
        finalValue = String(Math.round(parseFloat(value)));
      } else {
        // Allow digits and the decimal point (e.g., '1.')
        finalValue = String(value).replace(/[^0-9.]/g, "");
      }

      // Real-time bounds validation
      if (finalValue !== "" && Number(finalValue) > 20) {
        setOrderError("Display Order cannot exceed 20");
      } else if (finalValue !== "" && Number(finalValue) < 1) {
        setOrderError("Display Order must be at least 1");
      } else {
        setOrderError("");
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]:
        typeof finalValue === "string" && name === "locationIds"
          ? finalValue.split(",")
          : finalValue,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const categoryName = formData.name ? formData.name.trim() : "";
    if (!categoryName) {
      setError("Category name is required");
      setLoading(false);
      return;
    }

    const orderVal = formData.order;
    if (
      !/^\d+$/.test(String(orderVal).trim()) ||
      Number(orderVal) > 20 ||
      Number(orderVal) < 1
    ) {
      setError("Display Order must be a valid integer between 1 and 20.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("type", formData.type);
    data.append("order", formData.order);
    data.append("isActive", formData.isActive);

    // // Map the category type to moduleType for API
    // let moduleType = 'Grocery'; // default
    // if (formData.type === 'Services') {
    //     moduleType = 'Service';
    // } else if (formData.type === 'Grocery') {
    //     moduleType = 'Grocery';
    // } else if (formData.type === 'Food') {
    //     moduleType = 'Food';
    // }
    // data.append('moduleType', moduleType);

    if (formData.locationIds && formData.locationIds.length > 0) {
      formData.locationIds.forEach((id) => data.append("locationIds[]", id));
    } else {
      data.append("locationIds[]", "0");
    }

    if (image) {
      data.append("categoryImages", image);
    }

    try {
      let response;
      if (isEdit) {
        response = await updateCategory(id, data);
      } else {
        response = await createCategory(data);
      }

      if (
        response.success ||
        response.id ||
        response._id ||
        response.name ||
        response.data
      ) {
        setSuccess(true);
        setTimeout(() => navigate("/categories"), 1500);
      } else {
        let msg = response.message || "Failed to save category";
        // if (msg.toLowerCase().includes('exist') || msg.toLowerCase().includes('duplicate')) {
        //     msg = `the ${formData.name} already exist`;
        // }
        setError(msg);
      }
    } catch (err) {
      let msg =
        err.response?.data?.message ||
        "An error occurred while saving the category";
      // if (msg.toLowerCase().includes('exist') || msg.toLowerCase().includes('duplicate') || err.response?.status === 409) {
      //     msg = `the ${formData.name} already exist`;
      // }
      setError(msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/categories");
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            Categories
          </Link>
          <Typography color="text.primary">
            {isEdit ? "Edit Category" : "Create Category"}
          </Typography>
        </Breadcrumbs>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {isEdit ? "Modify Category" : "Create New Category"}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "text.border",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Category saved successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Image Upload Section */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Category Image
              </Typography>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "text.border",
                  borderRadius: 2,
                  height: 250,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  position: "relative",
                }}
              >
                {preview ? (
                  <>
                    <Box
                      component="img"
                      src={preview}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": { backgroundColor: "white" },
                      }}
                      onClick={() => {
                        setImage(null);
                        setPreview(null);
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <CloudUpload
                      sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Upload Category Image
                    </Typography>
                  </>
                )}
                <input
                  id="file-input-category"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    handleImageChange(e);
                    e.target.value = null;
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                Note: Image size must be 300px * 300px
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  variant="outlined"
                  sx={{ mt: 2, fontSize: "10px !important" }}
                  startIcon={<CloudUpload />}
                  onClick={() =>
                    document.getElementById("file-input-category").click()
                  }
                >
                  Upload Image
                </Button>
              </Box>
            </Grid>

            {/* Details Section */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Category Details
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Category Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. Fruits & Vegetables"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="category-type-label">Type</InputLabel>
                    <Select
                      labelId="category-type-label"
                      name="type"
                      value={formData.type}
                      label="Type"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="Grocery">Grocery</MenuItem>
                      <MenuItem value="Food">Food</MenuItem>
                      <MenuItem value="Services">Services</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Display Order"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    required
                    placeholder="1"
                    inputProps={{ step: "1" }}
                    error={Boolean(orderError)}
                    helperText={
                      orderError || "Enter a display order (maximum 20)"
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      ml: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={handleSwitchChange}
                          color="primary"
                        />
                      }
                      label={
                        formData.isActive ? "Active on App" : "Hidden from App"
                      }
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel id="location-select-label">
                      Service Locations
                    </InputLabel>
                    <Select
                      labelId="location-select-label"
                      name="locationIds"
                      multiple
                      size="small"
                      fontSize="10px"
                      value={formData.locationIds}
                      onChange={handleInputChange}
                      input={<OutlinedInput label="Service Locations" />}
                      open={isLocationMenuOpen}
                      onOpen={() => setIsLocationMenuOpen(true)}
                      onClose={() => setIsLocationMenuOpen(false)}
                      renderValue={(selected) =>
                        selected
                          .map(
                            (id) =>
                              serviceLocations.find(
                                // eslint-disable-next-line eqeqeq
                                (l) => (l.id || l._id) == id,
                              )?.name || id,
                          )
                          .join(", ")
                      }
                    >
                      {serviceLocations.map((loc) => (
                        <MenuItem
                          key={loc.id || loc._id}
                          value={loc.id || loc._id}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Checkbox
                            size="small"
                            checked={
                              formData.locationIds.indexOf(loc.id || loc._id) >
                              -1
                            }
                          />
                          <ListItemText primary={loc.name} />
                        </MenuItem>
                      ))}
                      {/* Custom Done Button */}
                      <Box
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          backgroundColor: "background.paper",
                          zIndex: 2,
                          p: 1,
                          textAlign: "right",
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsLocationMenuOpen(false);
                          }}
                        >
                          Done
                        </Button>
                      </Box>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                sx={{
                  mt: 5,
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate("/categories")}
                  disabled={loading}
                  sx={{ px: 4 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  disabled={loading || (!isEdit && !image)}
                  sx={{ px: 6, backgroundColor: "primary.main" }}
                >
                  {isEdit ? "Update Category" : "Create Category"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default ModifyCategory;
